import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { LocationState, LocationContext, Coordinates, WeatherData, LocationInfo } from '../../types/location';
import { OpenWeatherMapResponse } from '../../types/weather';
import { getCurrentLocation, checkLocationPermission } from '../../api-frontend/getCurrentLocation';
import { transformWeatherData, extractLocationInfo, formatCoordinatesForAPI } from '../../api-frontend/formatLocationContext';

const WEATHER_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
const LOCATION_CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

const initialState: LocationState = {
  currentLocation: null,
  isLocationEnabled: false,
  locationPermission: 'prompt',
  isLoading: false,
  error: null,
  lastLocationUpdate: 0,
  weatherCacheExpiry: WEATHER_CACHE_DURATION,
};

// Async thunk to check location permission
export const checkLocationPermissionStatus = createAsyncThunk(
  'location/checkPermission',
  async () => {
    return await checkLocationPermission();
  }
);

// Async thunk to get current location
export const fetchCurrentLocation = createAsyncThunk(
  'location/fetchCurrent',
  async (_, { rejectWithValue }) => {
    const result = await getCurrentLocation({
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: LOCATION_CACHE_DURATION,
    });

    if (!result.success) {
      return rejectWithValue(result.error);
    }

    return result.coordinates;
  }
);

// Async thunk to fetch weather data
export const fetchWeatherData = createAsyncThunk(
  'location/fetchWeather',
  async (coordinates: Coordinates, { rejectWithValue }) => {
    try {
      const apiCoordinates = formatCoordinatesForAPI(coordinates);
      
      const response = await fetch('/.netlify/functions/getWeatherData', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiCoordinates),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.error || 'Failed to fetch weather data');
      }

      const weatherResponse: OpenWeatherMapResponse = await response.json();
      const weatherData = transformWeatherData(weatherResponse);
      const locationInfo = extractLocationInfo(weatherResponse);

      return { weatherData, locationInfo };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Network error');
    }
  }
);

// Async thunk to get complete location context
export const fetchLocationContext = createAsyncThunk(
  'location/fetchContext',
  async (forceUpdate: boolean = false, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as { location: LocationState };
    const now = Date.now();
    
    // Check if we can use cached location
    const canUseCachedLocation = state.location.currentLocation && 
      (now - state.location.lastLocationUpdate) < LOCATION_CACHE_DURATION &&
      !forceUpdate;

    let coordinates: Coordinates;
    
    if (canUseCachedLocation) {
      coordinates = state.location.currentLocation.coordinates;
    } else {
      // Fetch fresh location
      const locationResult = await dispatch(fetchCurrentLocation()).unwrap();
      coordinates = locationResult;
    }

    // Check if we need to update weather data
    const needsWeatherUpdate = !state.location.currentLocation?.weather ||
      (now - (state.location.currentLocation.weather.timestamp || 0)) > state.location.weatherCacheExpiry ||
      forceUpdate;

    if (needsWeatherUpdate) {
      const weatherResult = await dispatch(fetchWeatherData(coordinates)).unwrap();
      return {
        coordinates,
        weatherData: weatherResult.weatherData,
        locationInfo: weatherResult.locationInfo,
      };
    } else {
      // Return existing weather data
      return {
        coordinates,
        weatherData: state.location.currentLocation.weather,
        locationInfo: state.location.currentLocation.locationInfo,
      };
    }
  }
);

const locationSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    setLocationEnabled: (state, action: PayloadAction<boolean>) => {
      state.isLocationEnabled = action.payload;
    },
    clearLocationError: (state) => {
      state.error = null;
    },
    updateLocationPermission: (state, action: PayloadAction<'granted' | 'denied' | 'prompt' | 'unavailable'>) => {
      state.locationPermission = action.payload;
    },
    setWeatherCacheExpiry: (state, action: PayloadAction<number>) => {
      state.weatherCacheExpiry = action.payload;
    },
    clearLocationData: (state) => {
      state.currentLocation = null;
      state.lastLocationUpdate = 0;
      state.error = null;
    },
    updateCoordinates: (state, action: PayloadAction<Coordinates>) => {
      if (state.currentLocation) {
        state.currentLocation.coordinates = action.payload;
        state.currentLocation.lastUpdated = Date.now();
        state.lastLocationUpdate = Date.now();
      } else {
        state.currentLocation = {
          coordinates: action.payload,
          lastUpdated: Date.now(),
        };
        state.lastLocationUpdate = Date.now();
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Check permission status
      .addCase(checkLocationPermissionStatus.fulfilled, (state, action) => {
        state.locationPermission = action.payload;
        state.isLocationEnabled = action.payload === 'granted';
      })
      
      // Fetch current location
      .addCase(fetchCurrentLocation.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCurrentLocation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lastLocationUpdate = Date.now();
        
        if (state.currentLocation) {
          state.currentLocation.coordinates = action.payload;
          state.currentLocation.lastUpdated = Date.now();
        } else {
          state.currentLocation = {
            coordinates: action.payload,
            lastUpdated: Date.now(),
          };
        }
      })
      .addCase(fetchCurrentLocation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to get location';
        
        // Update permission status based on error
        if (action.payload && typeof action.payload === 'object') {
          const error = action.payload as { code: number };
          if (error.code === 1) { // PERMISSION_DENIED
            state.locationPermission = 'denied';
            state.isLocationEnabled = false;
          }
        }
      })
      
      // Fetch weather data
      .addCase(fetchWeatherData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWeatherData.fulfilled, (state, action) => {
        state.isLoading = false;
        
        if (state.currentLocation) {
          state.currentLocation.weather = action.payload.weatherData;
          state.currentLocation.locationInfo = action.payload.locationInfo;
          state.currentLocation.lastUpdated = Date.now();
        }
      })
      .addCase(fetchWeatherData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to get weather data';
      })
      
      // Fetch location context
      .addCase(fetchLocationContext.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLocationContext.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lastLocationUpdate = Date.now();
        
        const { coordinates, weatherData, locationInfo } = action.payload;
        
        state.currentLocation = {
          coordinates,
          weather: weatherData,
          locationInfo,
          lastUpdated: Date.now(),
        };
      })
      .addCase(fetchLocationContext.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string || 'Failed to get location context';
      });
  },
});

export const {
  setLocationEnabled,
  clearLocationError,
  updateLocationPermission,
  setWeatherCacheExpiry,
  clearLocationData,
  updateCoordinates,
} = locationSlice.actions;

export default locationSlice.reducer;